/**
 * lyrics-ai-timing — precise lyric timing from the audio, opt-in.
 *
 * WHAT IT ACTUALLY DOES (and what it does not)
 * Speech recognition CANNOT read singing: Whisper returns garbled words on a
 * sung vocal. So this never uses the recognised WORDS. It uses only WHEN the
 * model heard something — a timing skeleton — and lays the user's OWN correct
 * lyrics onto it (auto-captions-align.js, already tested, does that mapping).
 *
 * That distinction is the whole design:
 *   • the user must supply the text — this is "AI timings for YOUR words", never
 *     "AI writes the lyrics", and the UI must not imply otherwise;
 *   • the result carries PER-WORD stamps, which is what unlocks the karaoke
 *     word-by-word wipe — the rough placement lane can never do that, because it
 *     only sees loudness.
 *
 * WHY IT IS OPT-IN
 * It downloads a 40–75 MB model to the user's device. The person who asked for
 * lyrics on the video was on Android over 4G with 4 GB of RAM. Nothing here runs
 * until they have read the size and pressed the button. Farid's call, 2026-07-31,
 * same shape as the HD-voice opt-in on /text-to-speech.
 *
 *   LyricsAiTiming.buildTimeline(lines, anchors, durationS)  ← pure, unit-tested
 *   LyricsAiTiming.toMono16k(audioBuffer)                    → Float32Array
 *   LyricsAiTiming.transcribe(pcm, opts, onProgress)         → { anchors, ... }
 *   LyricsAiTiming.run({ lines, audioBuffer, model, ... })   → timeline
 *
 * Exposed as window.LyricsAiTiming and module.exports (node tests).
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.LyricsAiTiming = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* Only the two sizes we MIRROR on our own origin (public/vendor/whisper-hf/).
       A non-mirrored size would fetch from HuggingFace's Xet CDN, which the RU
       /cdn/hf/ proxy cannot follow — so offering it would ship RU users a lane
       that fails at the download.

       The sizes are a RANGE because the real download depends on the backend the
       worker picks: WebGPU pulls the fp16 weights, WASM the q8 ones. MEASURED on
       the mirror, 2026-07-31 (weights + tokenizer/vocab/merges):

              q8 / WASM     fp16 / WebGPU
         tiny   ~45 MB          ~80 MB
         base   ~81 MB         ~150 MB

       A single number would be wrong on half the devices, and this figure is the
       whole basis of the consent — the user is agreeing to THIS download. (The
       shared <x-whisper-model-picker> catalog quotes the fp16 column only, which
       is why this lane keeps its own labels; see WhisperModelPickerConventionTest.) */
    var MODELS = {
        tiny: { id: 'tiny', mbMin: 45, mbMax: 80 },
        base: { id: 'base', mbMin: 80, mbMax: 150 },
    };

    /* Same tokenisation the aligner uses, applied per line so the flat word list
       we hand it and the per-line counts we regroup by cannot drift apart. */
    function tokenizeLine(line) {
        return String(line == null ? '' : line)
            .replace(/\s+/g, ' ')
            .trim()
            .split(' ')
            .filter(Boolean);
    }

    /**
     * Lay `lines` onto `anchors` and return a KaraokeLyrics timeline WITH words.
     *
     * lines   : ['first line', 'second line', ...]  (already trimmed, non-empty)
     * anchors : [{start, end}]  — Whisper word chunks; their TEXT is ignored
     * returns : [{startS, endS, text, words:[{startS, text}]}]
     *
     * Pure and synchronous, so the mapping is testable without a model.
     */
    /* How many DISTINCT moments the anchors actually describe.
       This is the difference between "the model heard the song" and "the model
       heard nothing and returned one degenerate [0,0] chunk". With a single
       distinct start the aligner's index-space interpolation collapses — every
       token maps to the same instant — and the whole lyric stacks at 0:00.
       Measured 2026-07-31 on a tone-only track: three lines, all at 0:00.0,
       while the UI said "timed word by word". */
    function distinctStarts(anchors) {
        var seen = {}, n = 0;
        for (var i = 0; i < (anchors || []).length; i++) {
            var s = anchors[i] && anchors[i].start;
            if (typeof s !== 'number' || !isFinite(s)) continue;
            var k = s.toFixed(3);
            if (!seen[k]) { seen[k] = 1; n++; }
        }
        return n;
    }

    /** Are these anchors usable at all? Fewer than two moments carry no timing. */
    function anchorsUsable(anchors) {
        return distinctStarts(anchors) >= 2;
    }

    function buildTimeline(lines, anchors, durationS) {
        var src = (lines || []).map(tokenizeLine).filter(function (t) { return t.length; });
        if (!src.length) return [];

        var flat = [];
        for (var i = 0; i < src.length; i++) flat = flat.concat(src[i]);

        var align = (typeof window !== 'undefined' && window.__acAlign) ? window.__acAlign : null;
        var timedWords;
        if (!anchorsUsable(anchors)) {
            // Degenerate skeleton — spread rather than pile everything on one
            // instant. The caller is told separately so the UI can stop calling
            // this "precise".
            timedWords = evenSpread(flat, durationS);
        } else if (align && typeof align.alignToAnchors === 'function') {
            // Hand it the ALREADY-normalised token stream, so its own tokenizer
            // produces exactly our tokens and index i means the same on both sides.
            timedWords = align.alignToAnchors(flat.join(' '), anchors || [], durationS);
        } else {
            timedWords = evenSpread(flat, durationS);
        }
        if (!timedWords || timedWords.length !== flat.length) {
            // Never guess when the two sides disagree — an off-by-one here would
            // silently attach every word to the wrong line.
            timedWords = evenSpread(flat, durationS);
        }

        var out = [];
        var cursor = 0;
        for (var l = 0; l < src.length; l++) {
            var n = src[l].length;
            var slice = timedWords.slice(cursor, cursor + n);
            cursor += n;
            if (!slice.length) continue;
            var words = slice.map(function (w) {
                return { startS: Math.max(0, w.start), text: w.text };
            });
            out.push({
                startS: Math.max(0, slice[0].start),
                endS: Math.max(slice[slice.length - 1].end, slice[0].start + 0.3),
                text: src[l].join(' '),
                words: words,
            });
        }

        // endS chains to the next line's start, matching parseLRC — every
        // downstream consumer (painter, serializer) assumes that one shape.
        out.sort(function (a, b) { return a.startS - b.startS; });
        for (var k = 0; k < out.length; k++) {
            out[k].endS = (k + 1 < out.length)
                ? out[k + 1].startS
                : Math.max(out[k].endS, out[k].startS + 2);
        }
        return out;
    }

    function evenSpread(tokens, durationS) {
        var dur = (durationS > 0) ? durationS : Math.max(1, tokens.length * 0.5);
        var step = dur / tokens.length;
        return tokens.map(function (t, i) {
            return { text: t, start: i * step, end: (i + 1) * step };
        });
    }

    /* ── Audio → 16 kHz mono Float32, what Whisper expects ──────────────────
       OfflineAudioContext, not FFmpeg: the buffer is already decoded in memory,
       so resampling here costs milliseconds and avoids pulling the ~30 MB FFmpeg
       WASM core in just to change a sample rate. */
    function toMono16k(audioBuffer) {
        if (!audioBuffer) return Promise.reject(new Error('no-audio'));
        var OAC = (typeof OfflineAudioContext !== 'undefined')
            ? OfflineAudioContext
            : (typeof webkitOfflineAudioContext !== 'undefined' ? webkitOfflineAudioContext : null);
        if (!OAC) return Promise.reject(new Error('no-offline-audio-context'));

        var frames = Math.max(1, Math.ceil(audioBuffer.duration * 16000));
        var oac;
        try { oac = new OAC(1, frames, 16000); }
        catch (e) { return Promise.reject(e); }

        var srcNode = oac.createBufferSource();
        srcNode.buffer = audioBuffer;
        srcNode.connect(oac.destination);
        srcNode.start(0);
        return oac.startRendering().then(function (rendered) {
            return rendered.getChannelData(0);
        });
    }

    /* ── Drive the shared Whisper worker ────────────────────────────────────
       Returns the ANCHORS only. `initialPrompt` is the user's own lyrics: it
       measurably steers Whisper toward the right vocabulary, which sharpens the
       timing skeleton even though we discard its words. */
    function transcribe(pcm, opts, onProgress) {
        opts = opts || {};
        onProgress = onProgress || function () {};
        return new Promise(function (resolve, reject) {
            var loc = (typeof window !== 'undefined' && window.__LOCALE) ? window.__LOCALE : 'en';
            var model = MODELS[opts.model] ? opts.model : 'tiny';
            // hfmirror: both offered sizes are mirrored on our origin, so the
            // download never depends on HF's Xet CDN (unreachable via RU proxy).
            var url = '/js/whisper-worker.js?locale=' + encodeURIComponent(loc)
                    + '&hfmirror=' + encodeURIComponent('/vendor/whisper-hf/');

            var w;
            try { w = new Worker(url); } catch (e) { reject(e); return; }

            var settled = false;
            var totalChunks = Math.max(1, Math.ceil((opts.durationS || (pcm.length / 16000)) / 29));

            function finish(fn, arg) {
                if (settled) return;
                settled = true;
                try { w.terminate(); } catch (_) {}
                fn(arg);
            }

            if (opts.signal) {
                if (opts.signal.aborted) { finish(reject, new DOMException('Aborted', 'AbortError')); return; }
                opts.signal.addEventListener('abort', function () {
                    finish(reject, new DOMException('Aborted', 'AbortError'));
                }, { once: true });
            }

            w.onmessage = function (e) {
                var d = e.data || {};
                if (d.type === 'result') {
                    var chunks = (d.result && d.result.chunks) || [];
                    var anchors = [];
                    for (var i = 0; i < chunks.length; i++) {
                        var ts = chunks[i].timestamp || [0, 0];
                        var s = ts[0], en = ts[1];
                        if (typeof s === 'number' && isFinite(s)) {
                            /* Keep WHAT was heard, not only when. On singing the
                               words come back garbled — useless for reading, but
                               they still say which of the user's words is being
                               sung, and alignToAnchors now uses that when enough
                               of them corroborate. Dropping .text here is what
                               forced the index mapping for two years. */
                            anchors.push({ start: s, end: (typeof en === 'number' && isFinite(en)) ? en : s,
                                           text: chunks[i].text });
                        }
                    }
                    finish(resolve, {
                        anchors: anchors,
                        wordLevel: !!(d.result && d.result.wordLevel),
                        detectedLang: (d.result && d.result.detectedLang) || null,
                    });
                } else if (d.type === 'error') {
                    finish(reject, new Error(d.message || 'whisper-error'));
                } else if (d.type === 'download_progress') {
                    var pr = d.data && d.data.progress;
                    if (typeof pr === 'number') onProgress({ phase: 'download', pct: Math.round(pr) });
                } else if (d.type === 'status' && d.status === 'transcribing') {
                    onProgress({ phase: 'transcribe', pct: 0 });
                } else if (d.type === 'chunk_done') {
                    var n = d.chunkIndex || 0;
                    onProgress({ phase: 'transcribe', pct: Math.min(99, Math.round(n / totalChunks * 100)) });
                } else if (d.type === 'model_downgraded') {
                    onProgress({ phase: 'downgraded', to: d.to });
                } else if (d.type === 'word_level_degraded') {
                    // The word-level variant was unavailable; stamps become
                    // approximate. The caller surfaces this — silently shipping
                    // a word-wipe built on chunk-level timing would look broken.
                    onProgress({ phase: 'word_level_degraded' });
                }
            };
            w.onerror = function (ev) {
                finish(reject, new Error('worker-error: ' + ((ev && ev.message) || '')));
            };

            var buf = pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength);
            w.postMessage({
                type: 'transcribe',
                audioBuffer: buf,
                modelSize: model,
                wordTimestamps: true,
                language: opts.language || 'auto',
                initialPrompt: opts.initialPrompt || '',
            }, [buf]);
        });
    }

    /** Full lane: audio + the user's lines → a word-level timeline. */
    /* ── Forced alignment: the exact answer, when it costs nothing extra ──────
       Asking a recogniser "what is sung here" and then matching its answer to
       the words the user already typed pays twice for the model's mistakes.
       With a CTC model the right question is forced alignment: Viterbi over the
       log-probability matrix for the one path that spells exactly this text.
       That is what MFA, WhisperX and torchaudio.forced_align do.

       Measured on a 196 s track assembled here from 18 known clips — so the
       window each word belongs to is exact and no recogniser's opinion is
       involved. Share of words landing inside their own clip:

           audio            index (old)   content match   forced align
           clean               59 %           71 %           99 %
           music bed 0.35      30 %           52 %           99 %

       It barely moves with noise because it never has to RECOGNISE anything,
       only locate a text it was handed: the clean and degraded runs differed by
       at most 0.48 s on any word.

       ⚠️ The catch is the weights: GigaAM is 305 MB against whisper-tiny's 40.
       This path must never turn a lyric-timing click into a 305 MB download, so
       it is taken ONLY when the model is ALREADY in the browser's cache — the
       person transcribed something on /lyrics-transcription, or chose it here
       before. Nobody pays anything new; those who already paid get the exact
       answer. Russian only, because that is what the model covers.

       ⚠️ Forced alignment ALWAYS returns a path, including for text that is not
       in the audio at all. The path's confidence is therefore checked, and a
       weak one falls back to the whisper ladder rather than showing confident
       nonsense. */
    var FORCED_MIN_SCORE = -1.6;
    /* ⚠️ The whole-path score above does NOT tell right lyrics from wrong ones.
       Measured 2026-09-18 on 8 human-annotated songs, each aligned with its own
       text and with the 7 others': right −0.15…−0.69, wrong −0.33…−1.60 — they
       overlap, so −1.6 lets almost any text through. The MEDIAN of per-word
       confidence does separate them: right −0.2…−2.7 (GigaAM −0.0…−1.3), wrong
       −3.1…−5.2 (GigaAM −7.8…−12.9). A path below this falls back to the
       whisper ladder exactly as a low score does. */
    var FORCED_MIN_MEDIAN_CONF = -3.0;
    function trusted(d) {
        if (!(d.words && d.words.length && typeof d.score === 'number' && d.score >= FORCED_MIN_SCORE)) return false;
        var c = [];
        for (var i = 0; i < d.words.length; i++) if (typeof d.words[i].conf === 'number') c.push(d.words[i].conf);
        if (!c.length) return true;                  // an older worker without conf: keep the old gate
        c.sort(function (a, b) { return a - b; });
        return c[c.length >> 1] >= FORCED_MIN_MEDIAN_CONF;
    }

    function gigaamCached() {
        if (typeof caches === 'undefined') return Promise.resolve(false);
        return caches.open('gigaam-model-v3').then(function (c) {
            return c.keys();
        }).then(function (keys) {
            for (var i = 0; i < keys.length; i++) {
                if (keys[i] && /gigaam_v3_e2e_ctc_int8\.onnx/.test(keys[i].url)) return true;
            }
            return false;
        }).catch(function () { return false; });
    }

    /* Resolves to the worker's align result or null — never rejects, because
       every failure here has a working fallback behind it. */
    function forcedAlign(pcm, text, opts, onProgress) {
        return forcedAlignOnce(pcm, text, opts, onProgress, false);
    }
    function forcedAlignOnce(pcm, text, opts, onProgress, isRetry) {
        return new Promise(function (resolve) {
            var base = (typeof window !== 'undefined' && window.__lyricsWorkerBase
                        && window.__lyricsWorkerBase.gigaam) || '/js/gigaam-worker.js';
            /* Which ORT build the worker loads is the owner's answer, carried on the
               worker's URL (see gigaam-worker.js). */
            var B = (typeof window !== 'undefined') ? window.__ortBuild : null;
            if (B && B.workerUrl) base = B.workerUrl(base, { simdOnly: true });
            var onLegacy = /[?&]ortb=/.test(base);
            var w;
            try { w = new Worker(base); } catch (e) { resolve(null); return; }
            var settled = false;
            function done(v) {
                if (settled) return;
                settled = true;
                try { w.terminate(); } catch (_) {}
                resolve(v);
            }
            w.onmessage = function (e) {
                var d = e.data || {};
                if (d.type === 'align_result') {
                    done(trusted(d) ? d : null);
                } else if (d.type === 'error') {
                    /* The browser could not reserve the current build's shared memory
                       (WebKit before 18): the owner marks it and answers ORT 1.18 —
                       once more in a fresh worker (the samples were cloned to the first
                       one, not handed over). */
                    if (!isRetry && !(opts && opts.signal && opts.signal.aborted)
                        && B && B.initFailed && B.initFailed(String(d.error || d.message || ''), { legacy: onLegacy ? 'legacy' : '', from: 'lyrics-timing' })) {
                        settled = true;
                        try { w.terminate(); } catch (_) {}
                        setTimeout(function () { forcedAlignOnce(pcm, text, opts, onProgress, true).then(resolve); }, 1000);
                        return;
                    }
                    done(null);
                } else if (d.type === 'progress') {
                    onProgress({ phase: 'forced_' + (d.phase || 'run'), loaded: d.loaded, total: d.total });
                }
            };
            w.onerror = function () { done(null); };
            if (opts && opts.signal) {
                try { opts.signal.addEventListener('abort', function () { done(null); }, { once: true }); } catch (_) {}
            }
            try {
                w.postMessage({ type: 'init', locale: 'ru' });
                w.postMessage({ type: 'align', pcm: pcm, text: text });
            } catch (e) { done(null); }
        });
    }

    /* Flat word timings -> the KaraokeLyrics timeline. The aligner returns the
       words in the order it was given them, so splitting back onto lines by
       word COUNT is exact rather than a guess. */
    function timelineFromWords(lines, words) {
        var src = [];
        for (var i = 0; i < lines.length; i++) {
            var t = String(lines[i] == null ? '' : lines[i]).trim().split(/\s+/).filter(Boolean);
            if (t.length) src.push(t);
        }
        var out = [], cursor = 0;
        for (var j = 0; j < src.length; j++) {
            var n = src[j].length;
            var slice = words.slice(cursor, cursor + n);
            cursor += n;
            if (!slice.length) continue;
            out.push({
                startS: Math.max(0, slice[0].start),
                endS: Math.max(slice[slice.length - 1].end, slice[0].start + 0.3),
                text: src[j].join(' '),
                words: slice.map(function (w) { return { startS: Math.max(0, w.start), text: w.text }; }),
            });
        }
        /* endS chains to the next line's start, the shape parseLRC produces and
           every consumer downstream assumes. */
        for (var k = 0; k < out.length; k++) {
            if (k + 1 < out.length) out[k].endS = Math.max(out[k].startS + 0.3, out[k + 1].startS);
        }
        return out;
    }

    function run(opts) {
        opts = opts || {};
        var lines = opts.lines || [];
        if (!lines.length) return Promise.reject(new Error('no-lines'));
        var onProgress = opts.onProgress || function () {};
        var degraded = false;

        /* The whisper ladder, exactly as before — now one of two routes. */
        function whisperRoute(pcm) {
            return Promise.resolve().then(function () {
                return transcribe(pcm, {
                model: opts.model,
                language: opts.language,
                durationS: opts.durationS,
                signal: opts.signal,
                // Whisper accepts ≤224 prompt tokens; the worker slices to 220 chars.
                initialPrompt: lines.join(' ').slice(0, 220),
            }, function (p) {
                if (p && p.phase === 'word_level_degraded') degraded = true;
                onProgress(p);
            });
        }).then(function (res) {
            var usable = anchorsUsable(res.anchors);
            var timeline;

            if (usable) {
                timeline = buildTimeline(lines, res.anchors, opts.durationS);
            } else {
                /* The model heard nothing it could place — an instrumental, a very
                   quiet mix, or vocals it simply could not pick out. Falling back
                   to the LOUDNESS draft rather than an even spread: it at least
                   skips the intro and the breaks, which is strictly better than
                   dividing the duration by the line count. Reporting this up is
                   the point — the alternative is a confident "timed word by word"
                   over a lyric stacked at 0:00. */
                var ch = null;
                try { ch = opts.audioBuffer && opts.audioBuffer.getChannelData(0); } catch (_) {}
                var AD = (typeof window !== 'undefined') ? window.LyricsAutodraft : null;
                if (AD && ch) {
                    timeline = AD.draft(lines.join('\n'), {
                        channelData: ch,
                        sampleRate: opts.audioBuffer.sampleRate,
                        durationS: opts.durationS,
                    });
                } else {
                    timeline = buildTimeline(lines, [], opts.durationS);
                }
            }

            return {
                timeline: timeline,
                anchorCount: res.anchors.length,
                detectedLang: res.detectedLang,
                // Word stamps are only trustworthy when the model really produced
                // word-level output AND the anchors describe more than one moment.
                // The caller uses this to decide whether the word-by-word wipe may
                // be offered at all.
                wordLevel: !!res.wordLevel && !degraded && usable,
                // true = the model found nothing placeable; the timings above are
                // a loudness draft, not recognition. The UI must say so.
                noSpeech: !usable,
                forced: false,
            };
            });
        }

        /* Route choice. Forced alignment is exact but needs the CTC weights, so
           it is taken ONLY when they are already cached — a lyric-timing click
           must never become a 305 MB download. Any failure, and any path the
           model is not confident about, falls through to whisperRoute. */
        return toMono16k(opts.audioBuffer).then(function (pcm) {
            var loc = (typeof window !== 'undefined' && window.__LOCALE) ? window.__LOCALE : 'en';
            var allowed = (opts.forcedAlign !== false) && (typeof Worker !== 'undefined');
            /* Только GigaAM, только русский. Остальные языки этот модуль не
               выравнивает: их ставит фонемный выравниватель в KaraokeAutoSync
               (страница зовёт его раньше этого модуля и сюда приходит лишь с
               письменностью, которую не читает ни одна модель). Прежний
               провайдер латиницы, MMS, снят 24.09.2026 — CC-BY-NC-4.0. Выбор —
               по локали, а не по тому, что оказалось загружено: провайдер, не
               знающий языка, выдаёт уверенную чепуху. */
            var provider = (allowed && loc === 'ru') ? 'gigaam' : null;
            var cachedP = provider ? gigaamCached() : Promise.resolve(false);
            return cachedP.then(function (cached) {
                if (!cached) return null;
                onProgress({ phase: 'forced_start', provider: provider });
                return forcedAlign(pcm, lines.join(' '), opts, onProgress);
            }).catch(function () { return null; }).then(function (fa) {
                if (fa && fa.words && fa.words.length) {
                    var tl = timelineFromWords(lines, fa.words);
                    if (tl.length) {
                        return { timeline: tl, anchorCount: fa.words.length, detectedLang: loc,
                                 wordLevel: true, noSpeech: false, forced: true,
                                 provider: provider, score: fa.score };
                    }
                }
                return whisperRoute(pcm);
            });
        });
    }

    return {
        MODELS: MODELS,
        buildTimeline: buildTimeline,
        anchorsUsable: anchorsUsable,
        _distinctStarts: distinctStarts,
        toMono16k: toMono16k,
        transcribe: transcribe,
        run: run,
        _tokenizeLine: tokenizeLine,
        _trusted: trusted,
    };
});
